<template>
  <span v-if="chequeCheckerEnabled && (verdict || canCheck)" class="receipt-check">
    <small v-if="verdict" class="receipt-check__verdict">{{ verdict }}</small>
    <button
      v-if="canCheck"
      type="button"
      class="btn btn--2 receipt-check__btn"
      :disabled="pending"
      :title="checkedAtTitle"
      @click.prevent="check"
    >{{ $t('button.check_receipt') }}</button>
  </span>
</template>

<script lang="ts">
import {computed, defineComponent, PropType, ref} from "vue";
import {useStore} from "vuex";
import {useNotification} from "@kyvg/vue3-notification";
import {$t, helper} from "@/service/CommonHelper";
import {api} from "@/service/APIClient";
import {dateHelper} from "@/service/DateHelper";
import {findReceiptCheck, isPdfAttachment, receiptVerdict, upsertReceiptCheck} from "@/service/ReceiptCheckHelper";
import TraderDealDTO from "@/models/TraderDealDTO";
import AttachmentDTO from "@/models/AttachmentDTO";

export default defineComponent({
  methods: {$t},
  props: {
    deal: {type: Object as PropType<TraderDealDTO>, required: true},
    attachment: {type: Object as PropType<AttachmentDTO>, required: true},
  },
  setup(props) {
    const store = useStore()
    const notification = useNotification()
    const pending = ref(false)

    const chequeCheckerEnabled = computed(() => !!store.getters.getAppData?.CHEQUE_WALES_CHECKER_ENABLED)
    const canCheck = computed(() =>
      isPdfAttachment(props.attachment.file) && (store.getters.isTrader || store.getters.isTraderOperator)
    )
    const verdict = computed(() => receiptVerdict(props.deal, props.attachment.id))
    const checkedAtTitle = computed(() => {
      const check = findReceiptCheck(props.deal, props.attachment.id)
      return check ? dateHelper.format(check.checkedAt) : undefined
    })

    const check = async () => {
      if (pending.value) {
        return
      }

      pending.value = true
      try {
        upsertReceiptCheck(props.deal, await api.call.traderDeals.receiptCheck(props.deal.id, props.attachment.id))
      } catch (e) {
        if (helper.isValidationErrors(e)) {
          for (const item of helper.getValidationErrors(e)) {
            notification.notify({type: 'error', title: $t(`server_errors.${item.code}`, {}, item.message)})
          }
        }
      } finally {
        pending.value = false
      }
    }

    return {
      pending,
      chequeCheckerEnabled,
      canCheck,
      verdict,
      checkedAtTitle,
      check,
    }
  },
})
</script>

<style scoped>
.receipt-check {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin: 0 4px;
}

.receipt-check__btn {
  height: 28px;
  padding: 0 10px;
  font-size: 12px;
  white-space: nowrap;
}
</style>
